import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Job } from '../interfaces/job';
import { Offer } from '../interfaces/offer';
import { Bid } from '../interfaces/bid';
import { MarketplaceFilter } from '../interfaces/marketplaceFilter';
import { AuthService } from '../services/auth.service';
import { TopicService } from './topic.service';
import { MarketplacePublishOptions } from '../interfaces/marketplacePublishOptions';
import { Tenant } from '../interfaces/tenant';
import { EntityShareUrl } from '../types/EntityShareUrl';
import { Price } from '../interfaces/Price';

@Injectable({
  providedIn: 'root',
})
export class MarketplaceService {
  prefix = `${environment.serverUrl}/marketplace`;

  constructor(private http: HttpClient, private authService: AuthService, private topicService: TopicService) {}

  publish(jobId: string, options: MarketplacePublishOptions = null): Promise<Offer> {
    return this.http.post<Offer>(`${this.prefix}/publish/${jobId}`, options).toPromise();
  }
  publishContractJob(contractJobId: string, options: MarketplacePublishOptions = null): Promise<Job> {
    return this.http.post<Job>(`${this.prefix}/publish/contract-job/${contractJobId}`, options).toPromise();
  }
  publishSubConJob(job: Job, options: MarketplacePublishOptions = null): Promise<Job> {
    return this.http
      .post<Job>(`${this.prefix}/publish/subcon-job/`, { job, options })
      .toPromise();
  }
  updateSubConOffer(offer: Offer, circleIds: string[]): Promise<Job> {
    return this.http
      .put<Job>(`${this.prefix}/subcon/offers/${offer.id}`, { offer, circleIds })
      .toPromise();
  }
  getOffers(filter: MarketplaceFilter): Promise<Offer[]> {
    let params = new HttpParams();
    Object.keys(filter).forEach((key) => {
      if (filter[key]) params = params.append(key, filter[key]);
    });
    if (params.get('fromDate')) params = params.set('fromDate', filter.fromDate.toISOString());
    if (params.get('toDate')) params = params.set('toDate', filter.toDate.toISOString());
    return this.http
      .get<Offer[]>(`${this.prefix}/offers`, { params })
      .toPromise();
  }

  getSubconOffers(subconId: string, filter: MarketplaceFilter): Promise<Offer[]> {
    let params = new HttpParams();
    Object.keys(filter).forEach((key) => {
      if (filter[key]) params = params.append(key, filter[key]);
    });
    if (params.get('fromDate')) params = params.set('fromDate', filter.fromDate.toISOString());
    if (params.get('toDate')) params = params.set('toDate', filter.toDate.toISOString());
    return this.http
      .get<Offer[]>(`${this.prefix}/subcons/${subconId}/offers`, { params })
      .toPromise();
  }

  getMyOffers(filter?: MarketplaceFilter): Promise<{ offer: Offer; budgetPrice: Price; priceReference: Price }[]> {
    const find = [];
    if (filter?.fromDate) find.push(`fromDate=${filter.fromDate.toISOString()}`);
    if (filter?.toDate) find.push(`toDate=${filter.toDate.toISOString()}`);
    if (filter?.search) find.push(`search=${encodeURIComponent(filter.search)}`);
    if (filter?.bids) find.push(`bids=${filter.bids}`);
    if (filter?.onlyExpired) find.push(`onlyExpired=${filter.onlyExpired}`);
    if (filter?.filterByOrigin) find.push(`filterByOrigin=${filter.filterByOrigin}`);

    return this.http
      .get<{ offer: Offer; budgetPrice: Price; priceReference: Price }[]>(
        `${this.prefix}/offers/my${find.length > 0 ? '?' + find.join('&') : ''}`,
      )
      .toPromise();
  }

  getExpiredOffers(): Promise<Offer[]> {
    return this.http.get<Offer[]>(`${this.prefix}/offers/expired`).toPromise();
  }
  getActiveOffers(): Promise<Offer[]> {
    const biddedByTenantId = this.authService.getTenant();
    return this.http.get<Offer[]>(`${this.prefix}/offers/${biddedByTenantId}/active/`).toPromise();
  }
  getOfferById(offerId: string): Promise<Offer | null> {
    return this.http.get<Offer>(`${this.prefix}/offers/${offerId}`).toPromise();
  }
  getBidByOfferId(offerId): Promise<Bid> {
    return this.http.get<Bid>(`${this.prefix}/offers/${offerId}/bids/mine`).toPromise();
  }
  getBidsForOfferId(offerId): Promise<Bid[]> {
    return this.http.get<Bid[]>(`${this.prefix}/offers/${offerId}/bids`).toPromise();
  }
  addBid(offerId: string, bid: Bid): Promise<Bid> {
    return this.http.post<Bid>(`${this.prefix}/offers/${offerId}/bids`, bid).toPromise();
  }
  editBid(offerId: string, bid: Bid): Promise<Bid> {
    return this.http.put<Bid>(`${this.prefix}/offers/${offerId}/bids`, bid).toPromise();
  }
  addCounterBid(offerId: string, bid: Bid): Promise<Bid> {
    return this.http.put<Bid>(`${this.prefix}/offers/${offerId}/bids/counterBid`, bid).toPromise();
  }

  revokeBid(offerId: string, bidId: string): Promise<Bid> {
    return this.http.delete<Bid>(`${this.prefix}/offers/${offerId}/bids/${bidId}/remove`).toPromise();
  }

  acceptBid(offerId: string, bid: Bid): Promise<Job> {
    return this.http.post<Job>(`${this.prefix}/offers/${offerId}/bids/${bid.id}/accept`, bid).toPromise();
  }
  addToWatchList(offerId: string): Promise<any> {
    return this.http
      .post<string>(`${this.prefix}/offers/${offerId}/watch`, { offerId })
      .toPromise();
  }
  removeFromWatchList(jobId, offerWatchId: string): Promise<any> {
    return this.http.delete<string>(`${this.prefix}/offers/${jobId}/watch/${offerWatchId}`).toPromise();
  }
  isWatching(offerId: string): Promise<any> {
    return this.http.get<Bid>(`${this.prefix}/offers/${offerId}/isWatching`).toPromise();
  }
  updateOffer(offer: Offer, circleIds: any[] = null): Promise<Offer> {
    return this.http
      .put<Offer>(`${this.prefix}/offers/${offer.id}`, { offer, circleIds })
      .toPromise();
  }

  async subscribeToMarketplaceNotifications() {
    const tenant = this.authService.getTenant();
    const topics = [`marketplace-${tenant.id}`, 'marketplace'];
    this.topicService.subscribeToTopics(topics, () => {}).then(() => {});
  }

  async unsubscribeFromMarketplaceNotifications() {
    const tenant = this.authService.getTenant();
    const topics = [`marketplace-${tenant.id}`, 'marketplace'];
    await this.topicService.unsubscribeFromTopics(topics);
  }

  async cleanUpExpiredOffers(): Promise<{ updated: string[] }> {
    return this.http.get<{ updated: string[] }>(`${this.prefix}/offers/clean-up`).toPromise();
  }

  async getSubconsForHaulier(): Promise<Tenant[]> {
    return this.http.get<Tenant[]>(`${this.prefix}/subcons`).toPromise();
  }
  async deleteBid(offerId: string, bidId: string): Promise<Bid[]> {
    return this.http.delete<Bid[]>(`${this.prefix}/offers/${offerId}/bids/${bidId}`).toPromise();
  }
  async shareOfferUrl(offerId: string): Promise<EntityShareUrl> {
    return this.http.post<EntityShareUrl>(`${this.prefix}/offers/${offerId}/share-url`, {}).toPromise();
  }
}
